import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import { IconChevronDown, IconGripHorizontal } from '@tabler/icons-react';
import useApi from '../../hooks/useApi';
import Cookies from 'universal-cookie';
import ConfirmDialog from '../../commonComponent/ConfirmDialog';
import { getErrMessage } from '../../utils/CommonMethods';
import { updateGrantedAccountIdsInSnapshot } from '../../utils/accountContext/accountContextUtils';
import HeadphonesOutlinedIcon from '@mui/icons-material/HeadphonesOutlined';
import { TSM_BANNER_STORAGE_KEY } from '../../utils/tsmBannerStorage';

const cookies = new Cookies();

// Collapsed state and dragged position survive navigation, reload and re-login (CESMB-8399);
// clearCookies() keeps this key through logout
const BANNER_STORAGE_KEY = TSM_BANNER_STORAGE_KEY;
// Position is anchored to the bottom-right corner so expanding/collapsing keeps the same spot
const DEFAULT_BANNER_POSITION = { right: 24, bottom: 10 };
// CSS variable CommonDrawer subtracts from its height so drawer footers stay above the banner
const BANNER_OFFSET_CSS_VAR = '--tsm-banner-offset';
// Height of the band at the bottom of the viewport where drawer footers (action buttons) live
const DRAWER_FOOTER_ZONE = 80;
const BANNER_OFFSET_GAP = 8;

const readStoredBannerState = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(BANNER_STORAGE_KEY) || '{}');
    const position =
      Number.isFinite(stored?.position?.right) && Number.isFinite(stored?.position?.bottom) ? stored.position : DEFAULT_BANNER_POSITION;
    return { collapsed: stored?.collapsed === true, position };
  } catch (e) {
    return { collapsed: false, position: DEFAULT_BANNER_POSITION };
  }
};

const setBannerOffset = (px) => {
  if (typeof document === 'undefined') return;
  if (px > 0) {
    document.documentElement.style.setProperty(BANNER_OFFSET_CSS_VAR, `${px}px`);
  } else {
    document.documentElement.style.removeProperty(BANNER_OFFSET_CSS_VAR);
  }
};

/**
 * Technical Support Banner Component
 * Displays a fixed bottom bar when technical support access is granted
 */
const TechnicalSupportBanner = ({ isVisible, onEndAccess }) => {
  const theme = useTheme();
  const [endAccessModalOpen, setEndAccessModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => readStoredBannerState().collapsed);

  const [isDragging, setIsDragging] = useState(false);
  const bannerRef = useRef(null);
  const handleEndAccessClick = useCallback(() => {
    setEndAccessModalOpen(true);
  }, []);
  const didDragRef = useRef(false);
  const handleEndAccessConfirm = useCallback(async () => {
    setLoading(true);

    try {
      const customerId = cookies.get('userid') || cookies.get('userId');
      const secureCookie = process.env.secureCoockie || true;

      const data = await useApi(
        'END_SUPPORT_ACCESS',
        {},
        {
          data: {
            suppAccessSt: '0',
            customerId: customerId,
            accountId: cookies.get('accountId')
          }
        },
        []
      );

      // Success if status is true OR responseCode is "44005"
      if (data?.response?.status || data?.response?.responseCode === '44005') {
        // Set cookie
        cookies.set('supportAccessSt', '0', { secure: secureCookie, path: '/' });
        updateGrantedAccountIdsInSnapshot(cookies.get('accountId'), false);

        // Broadcast revocation so listeners (ProfileSection menu item,
        // any other widgets) can react without a refresh. MainLayout will
        // also hide the banner via its own listener; the parent callback
        // is kept for backward compatibility.
        if (typeof window !== 'undefined' && window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent('supportAccessRevoked'));
        }

        // Call the parent callback to update state
        if (onEndAccess) {
          onEndAccess();
        }
        setEndAccessModalOpen(false);
      } else {
        // Handle error
        const responseCode = data?.response?.responseCode;
        const errorMsg = getErrMessage(responseCode) || data?.response?.message || 'Error ending access';
        console.error(errorMsg);
        // You might want to show a snackbar here
        setEndAccessModalOpen(false);
      }
    } catch (error) {
      console.error('Error ending support access:', error);
      setEndAccessModalOpen(false);
    } finally {
      setLoading(false);
    }
  }, [onEndAccess]);

  const handleEndAccessClose = useCallback(() => {
    setEndAccessModalOpen(false);
  }, []);

  // Distance from the viewport's right/bottom edges; kept across collapse/expand
  const [dragPosition, setDragPosition] = useState(() => readStoredBannerState().position);

  const handleToggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  // Handle dragging logic
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging || !isCollapsed) return;
      didDragRef.current = true;
      const DRAG_SPEED = 1.5;

      setDragPosition((prev) => {
        const bannerWidth = bannerRef.current?.offsetWidth || 50;
        const bannerHeight = bannerRef.current?.offsetHeight || 52;

        const nextRight = prev.right - e.movementX * DRAG_SPEED;
        const nextBottom = prev.bottom - e.movementY * DRAG_SPEED;

        return {
          right: Math.max(0, Math.min(nextRight, window.innerWidth - bannerWidth)),
          bottom: Math.max(0, Math.min(nextBottom, window.innerHeight - bannerHeight))
        };
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      // The pill moves faster than the cursor, so the release often lands off the grip and no
      // post-drag click fires to consume didDragRef. Clear it once this event sequence is done,
      // so the click that does follow a drag (same task) is still ignored but the next one isn't.
      if (didDragRef.current) {
        setTimeout(() => {
          didDragRef.current = false;
        }, 0);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isCollapsed]);

  const handleDragStart = (e) => {
    if (!isCollapsed) return;

    e.stopPropagation();
    didDragRef.current = false;
    setIsDragging(true);

    e.preventDefault();
  };

  // If the banner is collapsed and the user clicks without dragging, toggle collapse to expand. If they dragged, do not toggle.
  const handleBannerClick = () => {
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }

    handleToggleCollapse();
  };
  // Remember collapsed state and position (written once a drag ends, not on every mousemove)
  useEffect(() => {
    if (isDragging) return;
    try {
      window.localStorage.setItem(BANNER_STORAGE_KEY, JSON.stringify({ collapsed: isCollapsed, position: dragPosition }));
    } catch (e) {
      // Storage unavailable (private mode / blocked): banner still works, state just isn't remembered
    }
  }, [isCollapsed, dragPosition, isDragging]);

  // Keep the banner on screen (expanding widens it; the window may have shrunk) and tell
  // CommonDrawer how much of the bottom of the viewport it covers, so drawer footers stay clickable
  const syncBannerLayout = useCallback(() => {
    const banner = bannerRef.current;
    if (isVisible !== '1' || !banner) {
      setBannerOffset(0);
      return;
    }

    const maxRight = Math.max(0, window.innerWidth - banner.offsetWidth);
    const maxBottom = Math.max(0, window.innerHeight - banner.offsetHeight);
    setDragPosition((prev) =>
      prev.right > maxRight || prev.bottom > maxBottom
        ? { right: Math.min(prev.right, maxRight), bottom: Math.min(prev.bottom, maxBottom) }
        : prev
    );

    const rect = banner.getBoundingClientRect();
    const coversDrawerFooter = rect.bottom > window.innerHeight - DRAWER_FOOTER_ZONE;
    setBannerOffset(coversDrawerFooter ? Math.ceil(window.innerHeight - rect.top + BANNER_OFFSET_GAP) : 0);
  }, [isVisible]);

  useLayoutEffect(() => {
    syncBannerLayout();
  }, [syncBannerLayout, isCollapsed, dragPosition]);

  useEffect(() => {
    window.addEventListener('resize', syncBannerLayout);
    return () => window.removeEventListener('resize', syncBannerLayout);
  }, [syncBannerLayout]);

  useEffect(() => () => setBannerOffset(0), []);

  if (isVisible !== '1') {
    return null;
  }
  return (
    <>
      <Box
        ref={bannerRef}
        id="technical-support-banner"
        onClick={handleBannerClick}
        sx={{
          position: 'fixed',
          right: `${dragPosition.right}px`,
          bottom: `${dragPosition.bottom}px`,
          width: 'max-content',
          backgroundColor: '#2B3749',
          color: 'white.paper',
          px: isCollapsed ? 1.5 : 2,
          py: isCollapsed ? 1.25 : 1,
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'flex-end' : 'center',
          boxShadow: theme.shadows[8],
          gap: 1.5,
          borderRadius: '16px'
        }}
      >
        <HeadphonesOutlinedIcon color="#00BFA5" sx={{ color: '#00BFA5' }} />
        {!isCollapsed && (
          <Typography variant="body1" sx={{ mr: 0, color: 'white.paper' }} id="technical-support-banner-message">
            <FormattedMessage id="technicalSupportBannerMessage" defaultMessage="Your NETGEAR account is shared with Technical Support." />
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }} id="technical-support-banner-actions">
          {/* // End Access Button */}
          {!isCollapsed && (
            <>
              <Button
                id="end-technical-support-access-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEndAccessClick();
                }}
                size={isCollapsed ? 'small' : 'medium'}
                sx={{
                  color: 'white.paper',
                  textTransform: 'none',
                  fontWeight: 500,
                  border: '1px solid',
                  borderColor: 'white.paper',
                  borderRadius: '4px',
                  px: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
                disabled={loading}
              >
                <Typography
                  id="end-technical-support-access-button-text"
                  sx={{ color: 'white.paper', fontSize: isCollapsed ? '0.76rem' : '0.875rem', whiteSpace: 'nowrap' }}
                >
                  <FormattedMessage id="endTechnicalSupportAccess" defaultMessage="End Technical Support Access" />
                </Typography>
              </Button>
              {/* // Collapse button and message (only when not collapsed) */}

              <IconButton
                id="technical-support-banner-collapse-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleCollapse();
                }}
                sx={{
                  color: 'white.paper',
                  p: 0.8,
                  borderRadius: '8px',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                <IconChevronDown stroke={1} size={18} />
              </IconButton>
            </>
          )}
          {/* // Show drag handle when collapsed */}
          {isCollapsed && (
            <IconButton
              id="technical-support-banner-expand-button"
              onMouseDown={handleDragStart}
              onClick={(e) => {
                // Handle the click here only: letting it bubble to the banner's onClick toggled twice
                e.stopPropagation();
                if (didDragRef.current) {
                  didDragRef.current = false;
                  return;
                }
                handleToggleCollapse();
              }}
              sx={{
                color: 'white.paper',
                p: 0.5,
                borderRadius: '8px',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                cursor: isDragging ? 'grabbing' : 'grab',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <IconGripHorizontal stroke={1} size={20} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* End Access Confirmation Dialog */}
      <ConfirmDialog
        open={endAccessModalOpen}
        onClose={handleEndAccessClose}
        onConfirm={handleEndAccessConfirm}
        title={{ id: 'endTechnicalSupportAccess' }}
        message={{ id: 'endTechnicalSupportAccessMessage' }}
        cancelText={{ id: 'cancel' }}
        confirmText={{ id: 'endAccess' }}
        confirmVariant="contained"
        confirmColor="primary"
        loading={loading}
        maxWidth="xs"
        fullWidth={false}
        titleProps={{
          sx: {
            fontWeight: 500,
            fontSize: '1.25rem',
            borderBottom: `1px solid ${theme.palette.mode === 'dark' ? '#29282D' : '#e0e0e0'}`,
            pb: 1.5,
            px: 3,
            pt: 0
          }
        }}
        contentProps={{
          sx: {
            pt: '20px !important',
            px: 3,
            pb: 2,
            '& .MuiDialogContentText-root': {
              textAlign: 'center'
            }
          }
        }}
        actionsProps={{
          sx: {
            px: 3,
            pb: 0,
            pt: 1.5,
            gap: 2,
            borderTop: `1px solid ${theme.palette.mode === 'dark' ? '#29282D' : '#e0e0e0'}`
          }
        }}
        dialogProps={{
          id: 'end-technical-support-access-dialog',
          PaperProps: {
            sx: {
              border: `1px solid ${theme.palette.mode === 'dark' ? '#29282D' : '#e0e0e0'}`,
              borderRadius: 2,
              minWidth: '400px'
            }
          }
        }}
      />
    </>
  );
};

export default TechnicalSupportBanner;
